import { _ManageGroupContext, CHAT_GROUP_ID_PARAM } from "../context/group";
import { _APP_DATA } from "../data/AppData";
import { Base } from "../ui/bases/Base";
import { _PROXIMIDAD_CTRL } from "../ui/controllers/ProximidadController";
import { _UpdateCurrentHashViewMobile } from "../utils/Device";
import { THashPath, UIs } from "./Routes";
import { _HashAssign, _HashReplace } from "./UIManager";

const SEPARATOR = '--';
export const EVENTPARAMSCHANGE = "urlparamschange";
const EVENTPATHCHANGE = "urlhashpathchange";

let lastInstance: Base;

export type HashEvent = CustomEvent<{
    Path: THashPath;
    Params: URLSearchParams;
}>;

export interface IHashInfo {
    Path: THashPath;
    Parmas: URLSearchParams;
    CompleteHash: string;
};

const INTERNAL_HISTORY: IHashInfo[] = [];
const UNSUPPORTED_GROUP_CHANGE_PATHS = ['eventsnooze', 'eventsfood', 'eventshygiene', 'eventsactivities', 'eventswin', 'eventsphotos', 'eventscomments', 'eventshumor', 'eventsevaluation'];
let firstLoad = true;

export async function _NavigationHashController(hash: string): Promise<boolean> {
    const hashInfo = _GetHashInfo(hash);
    const { Path, Parmas } = hashInfo;
    const { Path: LastPath, Parmas: LastParams } = GetLastHashInfo() || {};

    const urlGroupId = Parmas?.get(CHAT_GROUP_ID_PARAM);

    if (UNSUPPORTED_GROUP_CHANGE_PATHS.includes(Path) && urlGroupId && Number(urlGroupId) != _APP_DATA.userData.IdGrupo) {
        _HistoryBackEvalAndTry("default");
        return false;
    }

    if (UNSUPPORTED_GROUP_CHANGE_PATHS.includes(Path) && firstLoad) {
        _HashReplace("classroom");
        return false;
    }

    firstLoad = false;

    const view = _GetRouteView(Path as THashPath);

    if (!view) {
        console.warn("-d", "No view found", hash);
        _HistoryBackEvalAndTry("default");
        return false;
    }

    if (!(["login", "groups"] as THashPath[]).includes(Path)) {
        const validGroupContext = await _ManageGroupContext();
        if (!validGroupContext) {
            console.warn("No valid group context");
            _HistoryBackEvalAndTry("groups");
            return false;
        }
    }

    INTERNAL_HISTORY.push(hashInfo);

    if (Path != LastPath) {
        document.dispatchEvent(<HashEvent>new CustomEvent(EVENTPATHCHANGE, {
            detail: {
                Path: Path,
                Params: new URLSearchParams(Parmas),
            }
        }));
        CreateInstance(view);
    } else if (Parmas?.toString() != LastParams?.toString()) {
        document.dispatchEvent(<HashEvent>new CustomEvent(EVENTPARAMSCHANGE, {
            detail: {
                Path: Path,
                Params: new URLSearchParams(Parmas),
            }
        }));
        console.log("-d", "Solo cambiaron los params");
    }
    _UpdateCurrentHashViewMobile(Path);

    if (_APP_DATA.userData) {
        _PROXIMIDAD_CTRL._StartPolling(); // Iniciar polling de proximidad al cambiar de vista, si el usuario está logueado
    } else {
        _PROXIMIDAD_CTRL._Destroy();
    }
    return true;
}

export function _GetRouteView(path: THashPath) {
    const availableView = UIs[path];
    if (!availableView) return null;
    return availableView;
}

export function _HistoryBackEvalAndTry(alternativePath: THashPath | "default") {
    if (INTERNAL_HISTORY.length > 1) {
        history.back();
    } else {
        if (alternativePath !== "default") {
            window.location.replace(`#${alternativePath}`);
            return;
        }
        window.location.replace(_APP_DATA.userData.IdGrupo ? "#home" : "#groups")
    }
}

export function _AddOnHashPathChangeEventListener(callback: ((e: HashEvent) => void)) {
    document.addEventListener(EVENTPATHCHANGE, callback as any);
    return callback;
}

export function _RemoveOnHashPathChangeEventListener(callback: ((e: HashEvent) => void)) {
    document.removeEventListener(EVENTPATHCHANGE, callback as any);
}

export function _AddOnParamsChangeEventListener(callback: ((e: HashEvent) => void)) {
    document.addEventListener(EVENTPARAMSCHANGE, callback as any);
    return callback;
}

export function _RemoveOnParamsChangeEventListener(callback: ((e: HashEvent) => void)) {
    document.removeEventListener(EVENTPARAMSCHANGE, callback as any);
}

export function _SetParams(newParms: { [key: string]: string | number | null }, addToHistory: boolean = true) {
    const { Path, Parmas } = _GetHashInfo();
    const currentParams = Parmas || new URLSearchParams();
    let hasChanges = false;
    Object.keys(newParms).forEach(key => {
        const newValue = newParms[key];
        const currentValue = currentParams.get(key);
        const newValueStr = String(newValue);
        if (newValue === null) {
            if (currentParams.has(key)) {
                currentParams.delete(key);
                hasChanges = true;
            }
        } else if (currentValue !== newValueStr) {
            currentParams.set(key, newValueStr);
            hasChanges = true;
        }
    });

    if (!hasChanges) return;
    const paramsString = currentParams.toString();
    const newHash = paramsString ? `${Path}--${paramsString}` : Path;

    if (addToHistory) {
        _HashAssign(newHash as THashPath);
        return;
    }
    _HashReplace(newHash as THashPath);
}

export function _GetHashInfo(hash: string = _GetCleanHash(null)): IHashInfo {
    const [path, strParams] = hash.split(SEPARATOR);
    let params: URLSearchParams = undefined;

    strParams && (params = new URLSearchParams(strParams));

    return {
        Path: path as THashPath,
        Parmas: params,
        CompleteHash: hash,
    }
}

export function _LoadUIFromHash(hash: THashPath) {
    let clsUI = UIs[hash];

    if (clsUI) {
        CreateInstance(clsUI);
    } else {
        console.warn("No window found..")
    }
}

export function _LoadUIMain() {
    const hashUi: THashPath = _APP_DATA.userData.IdGrupo > 0
        ? "home"
        : "groups";
    _HashAssign(hashUi);
}

export function _GetCleanHash(hash: string | null) {
    return (hash !== null ? hash : window.location.hash).replace("#", "");
}

function GetLastHashInfo() {
    return INTERNAL_HISTORY[INTERNAL_HISTORY.length - 1];
}

function CreateInstance<clsUI extends Base>(cls: new () => clsUI): void {
    if (lastInstance instanceof cls) {
        return;
    }
    if (lastInstance) {
        lastInstance.OnDestroy();
    }

    lastInstance = new cls();
}